import React, { useState } from 'react';
import { useCommon } from 'src/domains/common/Common';
import { observer } from 'src/utils/mobx-react';

import { HomeIcon } from 'src/domains/layouts/shared/assets/icons/HomeIcon/HomeIcon';
import {
    SportsNavigationContent,
    SportsNavigationCurrent,
    SportsNavigationWrapper,
} from 'src/domains/layouts/webview/components/eventNavigation/SportNavigation.style';
import { Link } from '../router/NewLink';
import { CarouselEventAutoMap } from './CarouselEventNavigationState';
import { CurrentViewType } from './common';
import { useAppStateContext } from 'src/appState/AppState';
import { TranslationsStore } from 'src/domains/layouts/state/translationsStore/TranslationsStore';

type BreadcrumbKey = 'sports' | 'sport' | 'competition' | 'event';

type SportNavigationDataType = {
    name: BreadcrumbKey;
    url: string;
    label: string | JSX.Element;
};

interface PropsType {
    currentView: CurrentViewType;
}

export const SportsNavigation = observer('SportsNavigation', ({ currentView }: PropsType) => {
    const common = useCommon();
    const { appSportsBookState } = useAppStateContext();
    const [stateMap] = useState(() => new CarouselEventAutoMap(common));
    const state = stateMap.get(currentView);
    const { name } = currentView;
    const { active, competitionsData } = state.competitionState;
    const activeCompetition =
        name === 'event' || name === 'competition'
            ? competitionsData.find((comp) => comp.value === active) || null
            : null;

    if (activeCompetition === null) return null;

    const { sportId, label: competitionLabel } = activeCompetition;

    return (
        <SportsNavigationWrapper>
            <SportsNavigationContent>
                {(['sports', 'sport', 'competition', 'event'] as const).map((breadCrumbKey) => {
                    let url: SportNavigationDataType['url'] = '/';
                    let label: SportNavigationDataType['label'] = <HomeIcon />;
                    const isCurrent = breadCrumbKey === name;

                    switch (breadCrumbKey) {
                        case 'event': {
                            label = state.event?.name || '';
                            break;
                        }

                        case 'competition':
                            url = sportId ? state.urlToCompetition(Number(active), sportId) : '';
                            label = competitionLabel || '';
                            break;

                        case 'sport':
                            if (sportId) {
                                const specialSportState = appSportsBookState.specialSportsMap.get(sportId);
                                const { specialExpandForSport } = specialSportState;
                                const translationsStore = TranslationsStore.get(common);
                                const sportName =
                                    specialExpandForSport?.label ?? translationsStore.translateSport(sportId);
                                url = state.urlToSport(sportId);
                                label = sportName;
                            } else {
                                label = '';
                            }
                            break;
                    }

                    if (label === '') {
                        return null;
                    }

                    if (isCurrent) {
                        return <SportsNavigationCurrent key={breadCrumbKey}>{label}</SportsNavigationCurrent>;
                    }

                    return (
                        <Link
                            key={breadCrumbKey}
                            to={url}
                        >
                            {label}
                        </Link>
                    );
                })}
            </SportsNavigationContent>
        </SportsNavigationWrapper>
    );
});
